/**
 * Requirements state – list and selected requirement.
 * Placeholder until API is connected.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface RequirementItem {
  id: string | number;
  role_title?: string;
  status?: string;
  [key: string]: unknown;
}

export interface RequirementsOptions {
  total_count?: number;
  assigned_count?: number;
  draft_count?: number;
  in_hiring_count?: number;
  closed_count?: number;
  inactive_count?: number;
  filtered_count?: number;
}

export interface RequirementsState {
  list: RequirementItem[];
  totalCount: number;
  options: RequirementsOptions;
  selectedId: string | number | null;
  /** Fetched requirement detail by id (e.g. from job-details ?requirement_id=). Cleared when not needed. */
  requirementDetail: RequirementItem | null;
  /** True when user chose "Skip Requirement" (Owner/Admin only). Sent as is_without_requirement in create job/requirement API payload. */
  is_without_requirement: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: RequirementsState = {
  list: [],
  totalCount: 0,
  options: {},
  selectedId: null,
  requirementDetail: null,
  is_without_requirement: false,
  loading: false,
  error: null,
};

const requirementsSlice = createSlice({
  name: "requirements",
  initialState,
  reducers: {
    setRequirementsList: (state, action) => {
      state.list = action.payload;
    },
    setRequirementsTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setRequirementsOptions: (state, action) => {
      state.options = action.payload ?? {};
    },
    setSelectedRequirementId: (state, action) => {
      state.selectedId = action.payload;
    },
    setRequirementDetail: (state, action) => {
      state.requirementDetail = action.payload ?? null;
    },
    setWithoutRequirement: (state, action) => {
      state.is_without_requirement = Boolean(action.payload);
    },
    setRequirementsLoading: (state, action) => {
      state.loading = action.payload;
    },
    setRequirementsError: (state, action) => {
      state.error = action.payload;
    },
    /** Patch one requirement as job-posted (uses current state; avoids stale list closures). */
    markRequirementJobPosted: (
      state,
      action: PayloadAction<{
        requirementId: string | number;
        jobId?: string | number | null;
      }>,
    ) => {
      const { requirementId, jobId = null } = action.payload;
      const normalizedId = String(requirementId);
      const idx = state.list.findIndex((r) => String(r?.id) === normalizedId);
      if (idx === -1) return;
      const requirement = state.list[idx];
      const existingPostedState =
        requirement?.is_job_posted != null &&
        typeof requirement.is_job_posted === "object"
          ? (requirement.is_job_posted as Record<string, unknown>)
          : {};
      state.list[idx] = {
        ...requirement,
        is_job_posted: {
          ...existingPostedState,
          posted: true,
          job_id:
            jobId ?? (existingPostedState as { job_id?: unknown }).job_id ?? null,
        },
      };
    },
    /** Restore a list item after a failed optimistic mark (see withRequirementPostedRollback). */
    revertRequirementJobPosted: (
      state,
      action: PayloadAction<{
        requirementId: string | number;
        previousItem: RequirementItem;
      }>,
    ) => {
      const { requirementId, previousItem } = action.payload;
      const normalizedId = String(requirementId);
      const idx = state.list.findIndex((r) => String(r?.id) === normalizedId);
      if (idx === -1 || !previousItem) return;
      state.list[idx] = previousItem;
    },
  },
});

export const {
  setRequirementsList,
  setRequirementsTotalCount,
  setRequirementsOptions,
  setSelectedRequirementId,
  setRequirementDetail,
  setWithoutRequirement,
  setRequirementsLoading,
  setRequirementsError,
  markRequirementJobPosted,
  revertRequirementJobPosted,
} = requirementsSlice.actions;
export default requirementsSlice.reducer;
